import { useCallback, useMemo, useState } from 'react';
import { Box, Link } from '@mui/material';
import { Pagination, Table } from 'antd';
import dayjs from 'dayjs';
import { getShipping } from '../../../../api/shipping';
import AppButton from '../../../../components/ui/AppButton';
import { useGetShipping } from '../../../../hooks/useShipping';
import { handleDownloadExcel } from '../../../../libs/utils';
import ApproveDialog from './ApproveDialog';
import SearchFilter from './SearchFilter';
import TrackingDialog from './TrackingDialog';

const CompanyHistory = () => {
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [approveData, setApproveData] = useState({});
  const [confirmApproveDialog, setConfirmApproveDialog] = useState(false);
  const [trackingDialog, setTrackingDialog] = useState(false);
  const [shippingId, setShippingId] = useState('');

  const [query, setQuery] = useState({
    page: 1,
    limit: 20,
    sort_col: 'create_timestamp',
    sort_value: false,
  });
  const { data: shippingList, isLoading: isLoadingShippingList } = useGetShipping({
    ...query,
    search: submittedSearch || undefined,
  });

  const shippingListData = useMemo(() => {
    if (!shippingList?.Data?.data) return [];

    const rows = [];

    shippingList.Data.data.forEach((shipping) => {
      const rewards = shipping.list_reward || [];
      const shippingRowSpan = rewards.length;

      rewards.forEach((reward, rewardIndex) => {
        rows.push({
          id: crypto.randomUUID(),
          create_timestamp: shipping.create_timestamp,
          company_code: shipping.company_id?.code,
          company_name: shipping.company_id?.company_name,
          transaction_point: shipping.point,
          shipping_id: shipping._id,
          delivery: shipping.delivery,
          order_id: shipping.order_id,
          tracking_url: shipping.tracking_url,
          shipping_point: shipping.point,
          type_delivery: shipping.type_delivery,
          reward_name: reward.name,
          reward_sku: reward.sku,
          reward_quantity: reward.quantity,
          reward_quantity_req: reward.quantity_req,
          point_use: reward.point_use,
          point: reward.point,
          quantity: reward.quantity,
          address_name: shipping.address?.name || '',
          address_phone: shipping.address?.phonenumber || '',
          address_detail: shipping.address?.detail || '',
          address_subdistrict: shipping.address?.subdistrict || '',
          address_district: shipping.address?.district || '',
          address_province: shipping.address?.province || '',
          address_zip_code: shipping.address?.zip_code || '',
          shipping_row_span: shippingRowSpan,
          state: shipping.state,
          is_first_shipping_row: rewardIndex === 0,
        });
      });
    });

    return rows;
  }, [shippingList]);

  const handleApproveClick = useCallback((data) => {
    setApproveData(data);
    setConfirmApproveDialog(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'วันเวลา',
        dataIndex: 'create_timestamp',
        key: 'create_timestamp',
        sorter: true,
        width: 180,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value) => dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
      },
      {
        title: 'รหัสบริษัท',
        dataIndex: 'company_code',
        key: 'company_code',
        sorter: true,
        width: 150,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value) => value,
      },
      {
        title: 'ชื่อบริษัท',
        dataIndex: 'company_name',
        key: 'company_name',
        sorter: true,
        width: 220,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value) => value,
      },
      {
        title: 'รางวัลที่แลก',
        dataIndex: 'reward_name',
        key: 'reward_name',
        width: 250,
      },
      {
        title: 'จำนวนที่จัดส่ง',
        dataIndex: 'reward_quantity',
        key: 'reward_quantity',
        width: 120,
        align: 'center',
      },
      {
        title: 'คะแนนที่ใช้',
        dataIndex: 'point_use',
        key: 'point_use',
        width: 150,
        align: 'center',
        render: (_, row) => row.quantity * row.point || '-',
      },
      {
        title: 'Order ID',
        dataIndex: 'order_id',
        key: 'order_id',
        width: 150,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value, row) => {
          if (!row.is_first_shipping_row) return null;

          if (row.state === 'WAITING') {
            return (
              <AppButton variant="dangerStrong" onClick={() => handleApproveClick(row)}>
                อนุมัติ
              </AppButton>
            );
          }
          return value;
        },
      },
      {
        title: 'Tracking',
        dataIndex: 'tracking_url',
        key: 'tracking_url',
        width: 150,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value, row) => {
          if (!row.is_first_shipping_row) return null;

          let content = '-';

          if (row.delivery) {
            content = row.tracking_url ? (
              <Link href={row.tracking_url} target="_blank">
                {row.delivery}
              </Link>
            ) : (
              row.delivery
            );
          } else if (row.type_delivery === 'self delivery') {
            if (row.state === 'WAITING') {
              content = '-';
            } else {
              return (
                <AppButton
                  variant="primary"
                  onClick={() => {
                    setTrackingDialog(true);
                    setShippingId(row.shipping_id);
                  }}
                >
                  Add Tracking
                </AppButton>
              );
            }
          }

          return content;
        },
      },
      {
        title: 'ชื่อผู้รับสินค้า',
        dataIndex: 'address_name',
        key: 'address_name',
        width: 150,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value) => value || '-',
      },
      {
        title: 'เบอร์ผู้รับสินค้า',
        dataIndex: 'address_phone',
        key: 'address_phone',
        width: 150,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value) => value || '-',
      },
      {
        title: 'ที่อยู่ผู้รับสินค้า',
        dataIndex: 'address_detail',
        key: 'address_detail',
        width: 275,
        align: 'center',
        onCell: (row) => ({
          rowSpan: row.is_first_shipping_row ? row.shipping_row_span : 0,
        }),
        render: (value, row) => {
          const address =
            [
              row.address_detail,
              row.address_subdistrict,
              row.address_district,
              row.address_province,
              row.address_zip_code,
            ]
              .filter(Boolean)
              .join(' ') || '-';

          return address;
        },
      },
    ],
    [handleApproveClick]
  );

  const handleDownload = useCallback(async () => {
    try {
      const response = await getShipping({
        page: 1,
        limit: 'all',
        sort_col: 'create_timestamp',
        sort_value: false,
      });

      if (response.Code === '20000') {
        const transaction = response.Data.data;

        const newTransaction = [];
        transaction.forEach((item) => {
          const address = item.address || {};
          const fullAddress =
            [
              address.detail,
              address.subdistrict,
              address.district,
              address.province,
              address.zip_code,
            ]
              .filter(Boolean)
              .join(' ') || '-';

          item.list_reward.forEach((reward) => {
            newTransaction.push({
              วันเวลา: dayjs(item.create_timestamp).format('DD/MM/YYYY HH:mm:ss'),
              รหัสร้านค้า: item.company_id.code || '-',
              ชื่อร้านค้า: item.company_id.company_name || '-',
              ตัวแทนจำหน่าย: item.dealer || '-',
              รางวัลที่แลก: reward.name || '-',
              จำนวน: reward.quantity || '-',
              คะแนนที่ใช้: reward.quantity * reward.point || '-',
              'Order ID': item.order_id || '-',
              tracking: item.tracking_url || '-',
              state: item.state || '-',
              ชื่อผู้รับสินค้า: address.name || '-',
              เบอร์ผู้รับสินค้า: address.phonenumber || '-',
              ที่อยู่ผู้รับสินค้า: fullAddress,
            });
          });
        });

        handleDownloadExcel(
          newTransaction,
          'Redeem History',
          `STARCARD_CLUB_REDEEM_${dayjs().format('DD-MMM-YYYY')}`,
          {
            header: [
              'วันเวลา',
              'รหัสร้านค้า',
              'ชื่อร้านค้า',
              'ตัวแทนจำหน่าย',
              'รางวัลที่แลก',
              'จำนวน',
              'คะแนนที่ใช้',
              'Order ID',
              'tracking',
              'state',
              'ชื่อผู้รับสินค้า',
              'เบอร์ผู้รับสินค้า',
              'ที่อยู่ผู้รับสินค้า',
            ],
          }
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  return (
    <Box>
      <Box
        sx={{
          padding: '16px',
          backgroundColor: 'white',
          borderBottomLeftRadius: '15px',
          borderBottomRightRadius: '15px',
          border: '2px solid white',
          boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
          '& .ant-pagination': {
            alignItems: 'center',
          },
          '& .ant-pagination-options': {
            alignSelf: 'center',
          },
        }}
      >
        <SearchFilter
          search={search}
          onSearchChange={setSearch}
          handleSearch={() => {
            setQuery((prev) => ({ ...prev, page: 1 }));
            setSubmittedSearch(search);
          }}
          handleDownload={handleDownload}
        />

        <Table
          bordered
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={shippingListData}
          loading={isLoadingShippingList}
          pagination={false}
          scroll={{
            x: 'max-content',
          }}
          rowHoverable={false}
          onChange={(pagination, filters, sorter) => {
            setQuery((prev) => ({
              ...prev,
              page: 1,
              sort_col: sorter.field,
              sort_value: sorter.order === 'ascend',
            }));
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <Pagination
            current={query.page}
            pageSize={query.limit}
            total={shippingList?.Data.total}
            onChange={(page) => setQuery((prev) => ({ ...prev, page }))}
          />
        </Box>

        <ApproveDialog
          open={confirmApproveDialog}
          onClose={() => setConfirmApproveDialog(false)}
          data={approveData}
        />
        <TrackingDialog
          open={trackingDialog}
          onClose={() => setTrackingDialog(false)}
          shippingId={shippingId}
        />
      </Box>
    </Box>
  );
};

export default CompanyHistory;
